interface SessionMetrics {
  sessionId: string;
  startTime: Date;
  endTime?: Date;
  messageCount: number;
  questionsAsked: string[];
  actionsCompleted: string[];
  satisfactionRatings: Array<{ messageId: string; rating: 'up' | 'down' }>;
}

interface AnalyticsData {
  totalSessions: number;
  totalMessages: number;
  averageSessionLength: number;
  commonQuestions: Record<string, number>;
  actionCompletionRate: number;
  satisfactionScore: number;
  engagementScore: number;
}

export class AgriAgentAnalytics {
  private currentSession: SessionMetrics | null = null;
  private sessions: SessionMetrics[] = [];

  constructor() {
    this.loadSessions();
  }

  private loadSessions(): void {
    const stored = localStorage.getItem('agriagent_analytics');
    if (stored) {
      const parsed = JSON.parse(stored);
      this.sessions = parsed.map((s: any) => ({
        ...s,
        startTime: new Date(s.startTime),
        endTime: s.endTime ? new Date(s.endTime) : undefined,
      }));
    }
  }

  private saveSessions(): void {
    localStorage.setItem('agriagent_analytics', JSON.stringify(this.sessions));
  }

  startSession(): void {
    this.currentSession = {
      sessionId: `session_${Date.now()}`,
      startTime: new Date(),
      messageCount: 0,
      questionsAsked: [],
      actionsCompleted: [],
      satisfactionRatings: [],
    };
  }

  endSession(): void {
    if (this.currentSession) {
      this.currentSession.endTime = new Date();
      this.sessions.push(this.currentSession);
      this.saveSessions();
      this.currentSession = null;

      // Keep only last 30 sessions
      if (this.sessions.length > 30) {
        this.sessions = this.sessions.slice(-30);
        this.saveSessions();
      }
    }
  }

  trackMessage(isUser: boolean, content?: string): void {
    if (!this.currentSession) {
      this.startSession();
    }

    if (this.currentSession) {
      this.currentSession.messageCount++;
      
      if (isUser && content) {
        this.currentSession.questionsAsked.push(content);
      }
    }
  }

  trackAction(action: string): void {
    if (this.currentSession) {
      this.currentSession.actionsCompleted.push(action);
    }
  }

  trackSatisfaction(messageId: string, rating: 'up' | 'down'): void {
    if (this.currentSession) {
      this.currentSession.satisfactionRatings.push({ messageId, rating });
    }
  }

  getAnalytics(): AnalyticsData {
    const totalSessions = this.sessions.length;
    const totalMessages = this.sessions.reduce((sum, s) => sum + s.messageCount, 0);
    
    // Calculate average session length (in minutes)
    const sessionLengths = this.sessions
      .filter((s) => s.endTime)
      .map((s) => ((s.endTime!.getTime() - s.startTime.getTime()) / 1000 / 60));
    const averageSessionLength = sessionLengths.length > 0
      ? sessionLengths.reduce((sum, len) => sum + len, 0) / sessionLengths.length
      : 0;

    // Count common questions
    const questionCounts: Record<string, number> = {};
    this.sessions.forEach((s) => {
      s.questionsAsked.forEach((q) => {
        const normalized = q.toLowerCase();
        questionCounts[normalized] = (questionCounts[normalized] || 0) + 1;
      });
    });

    // Calculate action completion rate
    const totalActions = this.sessions.reduce((sum, s) => sum + s.actionsCompleted.length, 0);
    const actionCompletionRate = totalMessages > 0 ? (totalActions / totalMessages) * 100 : 0;

    // Calculate satisfaction score
    const allRatings = this.sessions.flatMap((s) => s.satisfactionRatings);
    const upvotes = allRatings.filter((r) => r.rating === 'up').length;
    const satisfactionScore = allRatings.length > 0
      ? (upvotes / allRatings.length) * 100
      : 0;

    // Engagement score (based on messages per session and session frequency)
    const engagementScore = totalSessions > 0
      ? Math.min(100, (totalMessages / totalSessions) * 10)
      : 0;

    return {
      totalSessions,
      totalMessages,
      averageSessionLength,
      commonQuestions: questionCounts,
      actionCompletionRate,
      satisfactionScore,
      engagementScore,
    };
  }

  getEngagementScore(): number {
    const analytics = this.getAnalytics();
    return Math.round(analytics.engagementScore);
  }

  clearAnalytics(): void {
    this.sessions = [];
    this.currentSession = null;
    this.saveSessions();
  }

  exportAnalytics(): string {
    return JSON.stringify(this.getAnalytics(), null, 2);
  }
}

export const agentAnalytics = new AgriAgentAnalytics();
